import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  QrCodeIcon,
  CameraIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CubeIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { productService } from '../../services/productService'
import { serialService } from '../../services/serialService'
import toast from 'react-hot-toast'

export const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<'overview' | 'serials' | 'history' | 'purchase' | 'margins'>('overview')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [pendingSerials, setPendingSerials] = useState<string[]>([])
  const [scanInput, setScanInput] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const scanInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getById(id!),
  })

  const { data: priceHistoryData } = useQuery({
    queryKey: ['product', id, 'price-history'],
    queryFn: () => productService.getPriceHistory(id!),
    enabled: activeTab === 'history',
  })

  const { data: profitMarginsData } = useQuery({
    queryKey: ['product', id, 'profit-margins'],
    queryFn: () => productService.getProfitMargins(id!),
    enabled: activeTab === 'margins',
  })

  const deleteMutation = useMutation({
    mutationFn: () => productService.delete(id!),
    onSuccess: () => {
      toast.success('Product deleted successfully')
      navigate('/products')
    },
    onError: () => {
      toast.error('Failed to delete product')
    },
  })

  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => productService.uploadImageFile(id!, file),
    onSuccess: () => {
      toast.success('Image uploaded successfully')
      queryClient.invalidateQueries({ queryKey: ['product', id] })
      setUploadingImage(false)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload image')
      setUploadingImage(false)
    },
  })

  const bulkSerialMutation = useMutation({
    mutationFn: (serials: string[]) => serialService.createBulk(parseInt(id!), serials),
    onSuccess: (res: any) => {
      const { created = 0, skipped = 0 } = res.data?.data || {}
      toast.success(`Saved ${created} serial${created !== 1 ? 's' : ''}${skipped ? ` (${skipped} duplicate${skipped !== 1 ? 's' : ''} skipped)` : ''}`)
      setPendingSerials([])
      queryClient.invalidateQueries({ queryKey: ['product', id] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save serials')
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingImage(true)
    uploadImageMutation.mutate(file)
    e.target.value = ''
  }

  useEffect(() => {
    if (activeTab === 'serials') scanInputRef.current?.focus()
  }, [activeTab])

  const handleScanKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    const value = scanInput.trim()
    if (!value) return
    if (pendingSerials.includes(value)) {
      toast.error(`${value} already in list`)
    } else {
      setPendingSerials(prev => [...prev, value])
    }
    setScanInput('')
  }, [scanInput, pendingSerials])

  const product = data?.data?.data
  const priceHistory = priceHistoryData?.data?.data || []
  const profitMargins = profitMarginsData?.data?.data

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this product?')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Product not found</p>
        <Link to="/products" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to products
        </Link>
      </div>
    )
  }

  const stockSummary = product.stock_summary || []
  const availableStock = stockSummary.find((s: any) => s.status === 'available')?._count?.serial_id || 0
  const isLowStock = product.track_serial_numbers && availableStock <= product.reorder_level

  const purchaseHistory = product.purchase_history || []
  const latestSupplier = product.latest_supplier

  const priceChartData = [...priceHistory]
    .reverse()
    .map((entry: any) => ({
      date: new Date(entry.effective_date).toLocaleDateString(),
      cost: Number(entry.cost_price || 0),
      selling: Number(entry.selling_price || 0),
      wholesale: Number(entry.wholesale_price || 0),
      reason: entry.reason || '',
    }))

  const tabs = [
    { id: 'overview', label: 'Overview', icon: CubeIcon },
    { id: 'serials', label: 'Serial Numbers', icon: QrCodeIcon },
    { id: 'history', label: 'Price History', icon: ClockIcon },
    { id: 'purchase', label: 'Purchase History', icon: BuildingStorefrontIcon },
    { id: 'margins', label: 'Margins', icon: ChartBarIcon },
  ]

  const formatPrice = (price: number) => {
    if (!price || price === 0) return '-'
    return `KSh ${price.toLocaleString()}`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/products" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{product.product_name}</h1>
            <p className="text-sm text-gray-500">SKU: {product.sku}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/products/${id}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <PencilIcon className="w-4 h-4" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Alerts */}
      {isLowStock && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">Low Stock Alert</p>
            <p className="text-sm text-yellow-700">
              Only {availableStock} units remaining (reorder level: {product.reorder_level})
            </p>
          </div>
        </div>
      )}

      {/* Latest Supplier Banner */}
      {latestSupplier && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <BuildingStorefrontIcon className="w-5 h-5 text-blue-600" />
          <div>
            <p className="font-medium text-blue-800">Latest Supplier</p>
            <p className="text-sm text-blue-700">
              {latestSupplier.company_name} ({latestSupplier.supplier_code})
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Images & Basic Info */}
        <div className="space-y-6">
          {/* Main Image */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
              {product.product_images[0] ? (
                <img
                  src={product.product_images[0].image_url}
                  alt={product.product_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <CameraIcon className="w-24 h-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {product.product_images.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto">
                {product.product_images.map((img: any, idx: number) => (
                  <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                    <img src={img.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {uploadingImage ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                  Uploading...
                </>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4" />
                  Add Image
                </>
              )}
            </button>
          </div>

          {/* Stock Info */}
          {product.track_serial_numbers && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Stock Status</h3>
              <div className="space-y-3">
                {stockSummary.map((item: any) => (
                  <div key={item.status} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">{item.status}</span>
                    <span className="font-medium">{item._count.serial_id}</span>
                  </div>
                ))}
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">Total</span>
                    <span className="font-bold text-lg">
                      {stockSummary.reduce((sum: number, item: any) => sum + item._count.serial_id, 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl shadow border border-gray-100">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Brand</h3>
                    <p className="text-gray-900">{product.brands?.brand_name || 'N/A'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Category</h3>
                    <p className="text-gray-900">{product.categories?.category_name || 'N/A'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Barcode</h3>
                    <p className="text-gray-900">{product.barcode || 'N/A'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Unit</h3>
                    <p className="text-gray-900">{product.units?.unit_name || 'Piece'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Warranty</h3>
                    <p className="text-gray-900">
                      {product.warranty_months > 0 ? `${product.warranty_months} months` : 'No warranty'}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Track Serial Numbers</h3>
                    <p className="text-gray-900">
                      {product.track_serial_numbers ? 'Yes' : 'No'}
                    </p>
                  </div>
                  {product.weight_kg && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Weight</h3>
                      <p className="text-gray-900">{product.weight_kg} kg</p>
                    </div>
                  )}
                  {product.dimensions && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Dimensions</h3>
                      <p className="text-gray-900">{product.dimensions}</p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
                    <p className="text-gray-900">{product.description || 'No description available'}</p>
                  </div>
                  {product.track_serial_numbers && (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Reorder Level</h3>
                        <p className="text-gray-900">{product.reorder_level} units</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Reorder Quantity</h3>
                        <p className="text-gray-900">{product.reorder_quantity} units</p>
                      </div>
                    </>
                  )}
                  {/* Current Prices */}
                  {product.price_history?.[0] && (
                    <>
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <h3 className="text-sm font-medium text-gray-500 mb-3">Current Prices</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Cost Price</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatPrice(product.price_history[0].cost_price)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Selling Price</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatPrice(product.price_history[0].selling_price)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Wholesale</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatPrice(product.price_history[0].wholesale_price)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500">Corporate</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatPrice(product.price_history[0].corporate_price)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'serials' && (
                <div className="space-y-6">
                  {!product.track_serial_numbers ? (
                    <p className="text-gray-500 text-center py-8">Serial number tracking is not enabled for this product.</p>
                  ) : (
                    <>
                      {/* Barcode scan input */}
                      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <QrCodeIcon className="w-5 h-5 text-primary-600" />
                          Scan to Add Serials
                        </h3>
                        <div className="flex gap-2">
                          <input
                            ref={scanInputRef}
                            type="text"
                            value={scanInput}
                            onChange={e => setScanInput(e.target.value)}
                            onKeyDown={handleScanKeyDown}
                            placeholder="Scan or type serial number, press Enter..."
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            autoComplete="off"
                          />
                          <button
                            onClick={() => scanInputRef.current?.focus()}
                            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm text-gray-600"
                          >
                            Focus
                          </button>
                        </div>

                        {pendingSerials.length > 0 && (
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium text-gray-700">
                                {pendingSerials.length} serial{pendingSerials.length !== 1 ? 's' : ''} queued
                              </p>
                              <button
                                onClick={() => setPendingSerials([])}
                                className="text-xs text-red-500 hover:text-red-700"
                              >
                                Clear all
                              </button>
                            </div>
                            <div className="max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                              {pendingSerials.map((s, i) => (
                                <div key={i} className="flex items-center justify-between px-3 py-2">
                                  <span className="font-mono text-sm">{s}</span>
                                  <button
                                    onClick={() => setPendingSerials(prev => prev.filter((_, j) => j !== i))}
                                    className="text-gray-400 hover:text-red-500 ml-2"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() => bulkSerialMutation.mutate(pendingSerials)}
                              disabled={bulkSerialMutation.isPending}
                              className="mt-3 w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium text-sm"
                            >
                              {bulkSerialMutation.isPending
                                ? 'Saving...'
                                : `Save ${pendingSerials.length} Serial${pendingSerials.length !== 1 ? 's' : ''}`}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Existing serial numbers */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-gray-900">Recorded Serials</h3>
                          <Link to={`/inventory?product=${id}`} className="text-primary-600 hover:underline text-sm">
                            View all in inventory →
                          </Link>
                        </div>
                        {product.serial_numbers?.length === 0 ? (
                          <p className="text-gray-500 text-center py-8">No serial numbers recorded yet — scan some above</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead>
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial Number</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {product.serial_numbers?.map((serial: any) => (
                                  <tr key={serial.serial_id}>
                                    <td className="px-4 py-3 text-sm font-mono">{serial.serial_number}</td>
                                    <td className="px-4 py-3">
                                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                        serial.status === 'available' ? 'bg-green-100 text-green-800' :
                                        serial.status === 'sold' ? 'bg-blue-100 text-blue-800' :
                                        serial.status === 'consigned' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {serial.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{serial.current_location}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div className="space-y-6">
                  <h3 className="font-semibold text-gray-900">Price History</h3>

                  {/* Price Chart */}
                  {priceChartData.length > 1 && (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={priceChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip
                            formatter={(value: number) => `KSh ${value.toLocaleString()}`}
                            labelStyle={{ fontSize: 12 }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="cost" name="Cost Price" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="selling" name="Selling Price" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="wholesale" name="Wholesale" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {priceHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No price history available</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Selling Price</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wholesale</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {priceHistory.map((entry: any) => (
                            <tr key={entry.history_id}>
                              <td className="px-4 py-3 text-sm">
                                {new Date(entry.effective_date).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {entry.cost_price ? `KSh ${Number(entry.cost_price).toLocaleString()}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {entry.selling_price ? `KSh ${Number(entry.selling_price).toLocaleString()}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {entry.wholesale_price ? `KSh ${Number(entry.wholesale_price).toLocaleString()}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{entry.reason || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'purchase' && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Purchase History</h3>
                  {purchaseHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No purchase history available</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Landed Cost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {purchaseHistory.map((item: any, idx: number) => (
                            <tr key={idx}>
                              <td className="px-4 py-3 text-sm">
                                {item.purchases?.purchase_date
                                  ? new Date(item.purchases.purchase_date).toLocaleDateString()
                                  : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {item.purchases?.suppliers ? (
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {item.purchases.suppliers.company_name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {item.purchases.suppliers.supplier_code}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">{item.quantity}</td>
                              <td className="px-4 py-3 text-sm">
                                {item.unit_price ? `KSh ${Number(item.unit_price).toLocaleString()}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {item.landed_cost ? `KSh ${Number(item.landed_cost).toLocaleString()}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'margins' && (
                <div className="space-y-6">
                  <h3 className="font-semibold text-gray-900">Profit Margins</h3>
                  {!profitMargins ? (
                    <p className="text-gray-500 text-center py-8">Loading margins...</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                          <p className="text-sm text-green-600 mb-1">Retail Margin</p>
                          <p className="text-2xl font-bold text-green-800">
                            {profitMargins.margins?.retail?.percent?.toFixed(1)}%
                          </p>
                          <p className="text-sm text-green-700 mt-1">
                            KSh {profitMargins.margins?.retail?.margin?.toLocaleString()} per unit
                          </p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                          <p className="text-sm text-blue-600 mb-1">Wholesale Margin</p>
                          <p className="text-2xl font-bold text-blue-800">
                            {profitMargins.margins?.wholesale?.percent?.toFixed(1)}%
                          </p>
                          <p className="text-sm text-blue-700 mt-1">
                            KSh {profitMargins.margins?.wholesale?.margin?.toLocaleString()} per unit
                          </p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                          <p className="text-sm text-purple-600 mb-1">Corporate Margin</p>
                          <p className="text-2xl font-bold text-purple-800">
                            {profitMargins.margins?.corporate?.percent?.toFixed(1)}%
                          </p>
                          <p className="text-sm text-purple-700 mt-1">
                            KSh {profitMargins.margins?.corporate?.margin?.toLocaleString()} per unit
                          </p>
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="font-medium text-gray-900 mb-3">Suggested Selling Prices</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">30% Margin Target</p>
                            <p className="text-lg font-bold text-gray-900">
                              KSh {profitMargins.suggested_prices?.target_30_percent?.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">40% Margin Target</p>
                            <p className="text-lg font-bold text-gray-900">
                              KSh {profitMargins.suggested_prices?.target_40_percent?.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">50% Margin Target</p>
                            <p className="text-lg font-bold text-gray-900">
                              KSh {profitMargins.suggested_prices?.target_50_percent?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
